import React, { useState } from 'react';
import { DeleteFilled } from '@ant-design/icons';
import IconButton from 'components/@extended/IconButton';
import ChangeEmailModal from './ChangeEmailModal';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import 'react-widgets/scss/styles.scss';
import {
    errorMessage,
    nameRegExp,
    tenantTypes,
    userRoles,
    userTypes
} from 'utils/constants';
import OrgPropertiesService from '../../services/org-properties.service';
import OrgService from '../../services/org.service';
import ConfirmModal, { ConfirmModalType } from '../common/ConfirmModal';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';
import capitalize from '../../utils/capitalize';
import { Loader } from '../index';
import CountryCodeSelect from '../common/CountryCodeSelect';
import { openCompactModal } from '../../utils/modal/modal.helper';
import { hasModules } from '../../utils/org-modules';
import { moduleKeys } from '../../utils/permissions';

const CreateUpdateTenantModal = ({
    onClose,
    selectedTenant,
    property,
    type,
    orgId,
    activeOrg
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const guestsEnabled = hasModules(activeOrg, moduleKeys.GUESTS);
    const buildInvitePermission = values => guestsEnabled && !values.invite;
    const buildPermissions = values => {
        const permissions = [];
        if (buildInvitePermission(values)) permissions.push('invite');
        if (!values.calls) permissions.push('calls');
        return permissions;
    };

    const [confirmPayload, setConfirmPayload] = useState({
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        propertyId: '',
        phone_number: ''
    });

    const closeConfirm = () => setConfirmOpen(false);

    const buildPhoneNumber = (code, phoneRaw) => {
        if (!phoneRaw) return '';
        return `${code}${phoneRaw}`;
    };

    const getExistingUserPayloadEmail = values => {
        const formEmail = values.email?.trim().toLowerCase();
        if (formEmail) return formEmail;

        const existingUserEmail = confirmPayload.email?.trim().toLowerCase();
        return existingUserEmail || null;
    };

    const handleDelete = async () => {
        try {
            setSubmitting(true);
            await OrgPropertiesService.removeUserAccess(
                orgId,
                property.id,
                selectedTenant.uid
            );
            SnackbarNotify.success('Tenant removed successfully');
            onClose();
        } catch (err) {
            console.error('Error while removing tenant:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
            setDeleteConfirmOpen(false);
        }
    };

    const handleAddExistingUser = async values => {
        try {
            setSubmitting(true);

            const email = getExistingUserPayloadEmail(values);

            const inviteData = {
                first_name: values.first_name.trim().toLowerCase(),
                last_name: values.last_name.trim().toLowerCase(),
                email,
                phone: {
                    number: Number.parseInt(values.phone),
                    code: Number.parseInt(values.code),
                    country: values.country
                },
                role: userRoles.TENANT,
                type: userTypes.LONG_TERM,
                primary: values.primary,
                minor: values.minor,
                permissions: buildPermissions(values)
            };

            await OrgPropertiesService.addExistingUser(
                orgId,
                property.id,
                confirmPayload.uid,
                inviteData
            );

            SnackbarNotify.success('Invite sent — pending acceptance.');
            closeConfirm();
            onClose();
        } catch (err) {
            console.error('Error while adding existing user:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const submit = async values => {
        const isEdit = type === 'edit';

        if (isEdit) {
            try {
                setSubmitting(true);

                const userData = {
                    first_name: values.first_name.trim().toLowerCase(),
                    last_name: values.last_name.trim().toLowerCase(),
                    phone: {
                        number: Number.parseInt(values.phone),
                        code: Number.parseInt(values.code),
                        country: values.country
                    },
                    type: values.type,
                    primary: values.primary,
                    minor: values.minor,
                    suspended: Boolean(values.suspended),
                    permissions: {
                        calls: !values.calls,
                        invite: buildInvitePermission(values)
                    }
                };

                await OrgPropertiesService.updateUser(
                    orgId,
                    property.id,
                    selectedTenant.uid,
                    userData
                );

                SnackbarNotify.success('Tenant updated successfully');
                onClose();
                return;
            } catch (err) {
                console.error('Error while updating tenant:', err);
                SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
            } finally {
                setSubmitting(false);
            }

            return;
        }

        try {
            setSubmitting(true);

            const email = values.email.trim().toLowerCase();
            const phone_number = buildPhoneNumber(values.code, values.phone);

            const checkInvitePayload = {};
            if (email) checkInvitePayload.email = email;
            if (phone_number) checkInvitePayload.phone_number = phone_number;

            await OrgPropertiesService.checkInviteExists(
                orgId,
                property.id,
                checkInvitePayload
            );

            const checkUserPayload = {};
            if (email) checkUserPayload.email = email;
            if (phone_number) checkUserPayload.phone_number = phone_number;

            const userExists = await OrgService.checkUserExists(orgId, checkUserPayload);

            if (userExists?.uid) {
                if (userExists.property_ids?.includes(property.id)) {
                    throw new Error(
                        'This user is already linked to the selected property.'
                    );
                }

                setConfirmPayload({
                    uid: userExists.uid,
                    first_name: userExists?.first_name || '',
                    last_name: userExists?.last_name || '',
                    email: userExists?.email || '',
                    propertyId: property.id,
                    phone_number: userExists?.phone_number || ''
                });

                setConfirmOpen(true);
                return;
            }

            const inviteData = {
                first_name: values.first_name.trim().toLowerCase(),
                last_name: values.last_name.trim().toLowerCase(),
                phone: {
                    number: Number.parseInt(values.phone),
                    code: Number.parseInt(values.code),
                    country: values.country
                },
                role: userRoles.TENANT,
                type: userTypes.LONG_TERM,
                permissions: buildPermissions(values),
                primary: values.primary,
                minor: values.minor
            };

            if (email) inviteData.email = email;

            await OrgPropertiesService.createUserInvite(orgId, property.id, inviteData);

            SnackbarNotify.success('Invite sent — pending acceptance.');
            onClose();
        } catch (err) {
            console.error('Error while creating tenant:', err);
            SnackbarNotify.error(`${err?.message || 'Unexpected error'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues =
        type === 'edit'
            ? {
                  first_name: selectedTenant?.first_name?.trim() || '',
                  last_name: selectedTenant?.last_name?.trim() || '',
                  email: selectedTenant?.email?.trim() || '',
                  phone: selectedTenant?.phone?.number
                      ? `${selectedTenant.phone.number}`
                      : '',
                  code: selectedTenant?.phone?.code
                      ? `${selectedTenant.phone.code}`
                      : '1',
                  country: selectedTenant?.phone?.country
                      ? `${selectedTenant.phone.country}`
                      : 'US',
                  type:
                      selectedTenant?.properties?.[property.id]?.type ||
                      userTypes.LONG_TERM,
                  suspended: selectedTenant?.properties?.[property.id]?.suspended,
                  primary: selectedTenant?.properties?.[property.id]?.primary || false,
                  minor: selectedTenant?.properties?.[property.id]?.minor || false,
                  calls: !selectedTenant?.properties?.[property.id]?.permissions?.calls,
                  invite: !selectedTenant?.properties?.[property.id]?.permissions?.invite
              }
            : {
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                  code: '1',
                  country: 'US',
                  type: userTypes.LONG_TERM,
                  primary: true,
                  minor: false,
                  calls: false,
                  invite: false
              };

    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.firstName.valid)
            .required(errorMessage.firstName.required),
        last_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.lastName.valid)
            .required(errorMessage.lastName.required),
        email:
            type !== 'edit'
                ? Yup.string().email(errorMessage.email.valid)
                : Yup.string()
                      .trim()
                      .lowercase()
                      .email(errorMessage.email.valid)
                      .required(errorMessage.email.required),
        phone: Yup.string().trim().required(errorMessage.phone.required),
        country: Yup.string().trim(),
        code: Yup.string().trim(),
        suspended: Yup.boolean().notRequired(),
        calls: Yup.boolean(),
        invite: Yup.boolean(),
        type: Yup.string().oneOf(tenantTypes)
    });

    const validatePhoneNumber = values => {
        const number = values.phone;
        const code = values.code;
        const country = values.country;
        const phoneSchema = Yup.string().phone(country);

        if (!number) return {};

        try {
            phoneSchema.validateSync(`${code}${number}`);
            return {};
        } catch {
            return { phone: 'Phone number needs to be valid' };
        }
    };

    return (
        <>
            {submitting && <Loader />}
            <ConfirmModal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Confirm Delete"
                text={
                    <>
                        Are you sure you want to delete tenant{' '}
                        <strong>
                            {capitalize(selectedTenant?.first_name)}{' '}
                            {capitalize(selectedTenant?.last_name)}
                        </strong>
                        ?
                    </>
                }
                confirmText="Yes, Delete"
                cancelText="Cancel"
                type={ConfirmModalType.ERROR}
            />

            <DialogTitle>
                {type === 'edit' ? 'Edit Tenant' : 'Add New Tenant'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validate={validatePhoneNumber}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    handleSubmit,
                    handleChange,
                    touched,
                    setFieldValue,
                    handleBlur,
                    validateField,
                    errors,
                    values
                }) => {
                    const handleOpenChangeEmailModal = () => {
                        openCompactModal(
                            ChangeEmailModal,
                            {
                                currentEmail: values.email,
                                orgId,
                                propertyId: property?.id,
                                userId: selectedTenant?.uid,
                                onSuccess: newEmail => {
                                    setFieldValue('email', newEmail);
                                }
                            },
                            { fullWidth: true }
                        );
                    };

                    return (
                        <>
                            <ConfirmModal
                                open={confirmOpen}
                                onClose={closeConfirm}
                                onConfirm={() => handleAddExistingUser(values)}
                                title="Existing Sage Member Found"
                                text={
                                    <>
                                        User{' '}
                                        <strong>
                                            {capitalize(confirmPayload.first_name)}{' '}
                                            {capitalize(confirmPayload.last_name)}
                                        </strong>{' '}
                                        already exists.
                                        <br />
                                        <strong>Email:</strong> {confirmPayload.email}
                                        <br />
                                        <strong>Phone:</strong> +
                                        {confirmPayload.phone_number}
                                        <br />
                                        <br />
                                        Send this user an invite to the property? They
                                        will need to accept it before gaining access.
                                    </>
                                }
                                confirmText="Send Invite"
                                cancelText="Cancel"
                                type={ConfirmModalType.WARNING}
                            />

                            <DialogContent sx={{ p: 2.5 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>First Name</InputLabel>
                                            <TextField
                                                required
                                                id="firstNameBasic"
                                                name="first_name"
                                                placeholder="First Name"
                                                fullWidth
                                                autoComplete="given-name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    touched.first_name &&
                                                    !!errors.first_name
                                                }
                                                helperText={
                                                    touched.first_name &&
                                                    errors.first_name
                                                        ? errors.first_name
                                                        : ''
                                                }
                                                value={capitalize(values.first_name)}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Last Name</InputLabel>
                                            <TextField
                                                required
                                                id="lastNameBasic"
                                                name="last_name"
                                                placeholder="Last name"
                                                fullWidth
                                                autoComplete="family-name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={
                                                    touched.last_name &&
                                                    !!errors.last_name
                                                }
                                                helperText={
                                                    touched.last_name && errors.last_name
                                                        ? errors.last_name
                                                        : ''
                                                }
                                                value={capitalize(values.last_name)}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Email</InputLabel>
                                            <TextField
                                                required={type === 'edit'}
                                                id="emailBasic"
                                                name="email"
                                                placeholder="Email"
                                                fullWidth
                                                autoComplete="email"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                error={touched.email && !!errors.email}
                                                helperText={
                                                    touched.email && errors.email
                                                        ? errors.email
                                                        : ''
                                                }
                                                value={values.email}
                                                disabled={type === 'edit'}
                                                InputProps={{
                                                    readOnly: type === 'edit',
                                                    endAdornment:
                                                        type === 'edit' ? (
                                                            <InputAdornment position="end">
                                                                <Button
                                                                    variant="text"
                                                                    size="small"
                                                                    onClick={
                                                                        handleOpenChangeEmailModal
                                                                    }
                                                                    sx={{
                                                                        whiteSpace:
                                                                            'nowrap'
                                                                    }}
                                                                >
                                                                    Change Email
                                                                </Button>
                                                            </InputAdornment>
                                                        ) : null
                                                }}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={2} sm={2}>
                                        <Stack spacing={0.5}>
                                            <InputLabel htmlFor="country">
                                                Country Code
                                            </InputLabel>
                                            <CountryCodeSelect
                                                label=""
                                                size="medium"
                                                value={selectedCountry}
                                                defaultRegion={values.country || 'US'}
                                                onChange={selectedCountry => {
                                                    if (!selectedCountry) return;

                                                    setSelectedCountry(selectedCountry);

                                                    setFieldValue(
                                                        'code',
                                                        String(
                                                            selectedCountry.code
                                                        ).replace(/\D/g, '')
                                                    );
                                                    setFieldValue(
                                                        'country',
                                                        String(selectedCountry.region)
                                                    );

                                                    setTimeout(() =>
                                                        validateField('phone')
                                                    );
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={10} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Phone</InputLabel>
                                            <TextField
                                                required
                                                id="phoneBasic"
                                                name="phone"
                                                inputMode="numeric"
                                                onChange={e =>
                                                    setFieldValue(
                                                        'phone',
                                                        e.target.value.replace(/\D/g, '')
                                                    )
                                                }
                                                placeholder="Phone"
                                                fullWidth
                                                autoComplete="phone"
                                                onBlur={handleBlur}
                                                error={touched.phone && !!errors.phone}
                                                helperText={
                                                    touched.phone && errors.phone
                                                        ? errors.phone
                                                        : ''
                                                }
                                                value={values.phone}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={4}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={values.primary}
                                                            onChange={(_, checked) => {
                                                                setFieldValue(
                                                                    'primary',
                                                                    checked
                                                                );

                                                                if (checked) {
                                                                    setFieldValue(
                                                                        'minor',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'calls',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        false
                                                                    );
                                                                }
                                                            }}
                                                            disabled={values.minor}
                                                        />
                                                    }
                                                    label="Owner"
                                                />

                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={values.minor}
                                                            onChange={(_, checked) => {
                                                                setFieldValue(
                                                                    'minor',
                                                                    checked
                                                                );

                                                                if (checked) {
                                                                    setFieldValue(
                                                                        'primary',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'calls',
                                                                        true
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        true
                                                                    );
                                                                } else {
                                                                    setFieldValue(
                                                                        'calls',
                                                                        false
                                                                    );
                                                                    setFieldValue(
                                                                        'invite',
                                                                        false
                                                                    );
                                                                }
                                                            }}
                                                            disabled={values.primary}
                                                        />
                                                    }
                                                    label="Minor"
                                                />
                                            </Stack>
                                        </Stack>
                                    </Grid>

                                    {type === 'edit' && (
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Box>
                                                    <FormControlLabel
                                                        value="end"
                                                        control={
                                                            <Checkbox
                                                                checked={values.suspended}
                                                                onChange={() =>
                                                                    setFieldValue(
                                                                        'suspended',
                                                                        !values.suspended
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                sx={{ color: '#8c8c8c' }}
                                                            >
                                                                Suspended
                                                            </Typography>
                                                        }
                                                        labelPlacement="end"
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={12}>
                                        <Stack spacing={0.5}>
                                            <Box>
                                                <FormControlLabel
                                                    value="end"
                                                    control={
                                                        <Checkbox
                                                            checked={values.calls}
                                                            onChange={() =>
                                                                setFieldValue(
                                                                    'calls',
                                                                    !values.calls
                                                                )
                                                            }
                                                            disabled={values.minor}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            sx={{ color: '#8c8c8c' }}
                                                        >
                                                            Hide from calls
                                                        </Typography>
                                                    }
                                                    labelPlacement="end"
                                                    sx={{ ml: 0 }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    {guestsEnabled && (
                                        <Grid item xs={12} sm={12}>
                                            <Stack spacing={0.5}>
                                                <Box>
                                                    <FormControlLabel
                                                        value="end"
                                                        control={
                                                            <Checkbox
                                                                checked={values.invite}
                                                                onChange={() =>
                                                                    setFieldValue(
                                                                        'invite',
                                                                        !values.invite
                                                                    )
                                                                }
                                                                disabled={values.minor}
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                sx={{ color: '#8c8c8c' }}
                                                            >
                                                                Disable guest invites
                                                            </Typography>
                                                        }
                                                        labelPlacement="end"
                                                        sx={{ ml: 0 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>

                            <Divider />

                            <DialogActions sx={{ p: 2.5 }}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        {type === 'edit' && (
                                            <div>
                                                <Tooltip
                                                    title="Delete Tenant"
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        size="large"
                                                        color="error"
                                                        onClick={() =>
                                                            setDeleteConfirmOpen(true)
                                                        }
                                                        disabled={submitting}
                                                    >
                                                        <DeleteFilled />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </Grid>

                                    <Grid item>
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <Button
                                                color="error"
                                                onClick={() => {
                                                    onClose();
                                                }}
                                            >
                                                Cancel
                                            </Button>

                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={submitting}
                                                onClick={handleSubmit}
                                                id="add_update_tenant_button"
                                            >
                                                {type === 'edit'
                                                    ? 'Update'
                                                    : submitting
                                                    ? 'Processing...'
                                                    : 'Add'}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </DialogActions>
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default CreateUpdateTenantModal;
